import WorkflowSegments from "../bulletins/WorkflowSegments";
import { orderedWorkflowVersions } from "../onboarding/workflowVersions";
import { enqueueSnackbar } from "notistack";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link as RouterLink,
  Navigate,
  UNSAFE_NavigationContext,
  useParams,
} from "react-router-dom";
import {
  Add,
  ArrowBack,
  Delete,
  Pause,
  PlayArrow,
  Publish,
  StopCircle,
  Save,
  Verified,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useApiClient } from "../../../api";
import { useAuthenticationStore } from "../../../stores/auth";
import WorkflowCanvas from "../onboarding/WorkflowCanvas";
import WorkflowStageList from "../onboarding/WorkflowStageList";
import StageInspector from "../onboarding/StageInspector";
import ParticipantEnrollment from "../onboarding/ParticipantEnrollment";
import EmailPreviewDialog from "../onboarding/EmailPreviewDialog";
import { useWorkflowEditor } from "../onboarding/editor";
import ConfirmationDialog from "../onboarding/ConfirmationDialog";
import PublishedWorkflowOverview from "../onboarding/PublishedWorkflowOverview";
import WorkflowOperations from "../onboarding/WorkflowOperations";
import WorkflowSenderPool from "../onboarding/WorkflowSenderPool";
import WorkflowDeliverySchedule from "../onboarding/WorkflowDeliverySchedule";
import MarketingAutomationControl from "../onboarding/MarketingAutomationControl";
import VersionContinuation from "../onboarding/VersionContinuation";
import WorkflowMonitoring from "../onboarding/WorkflowMonitoring";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function useUnsavedChangesGuard(dirty) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [pendingTransition, setPendingTransition] = useState(null);
  useEffect(() => {
    if (!dirty || !navigator.block) return undefined;
    const unblock = navigator.block((transition) => {
      setPendingTransition({
        retry: () => {
          unblock();
          transition.retry();
        },
      });
    });
    return unblock;
  }, [dirty, navigator]);
  return {
    pendingTransition,
    stay: () => setPendingTransition(null),
    leave: () => {
      const transition = pendingTransition;
      setPendingTransition(null);
      transition?.retry();
    },
  };
}

export default function OnboardingWorkflowPage() {
  const { workflowId } = useParams();
  const api = useApiClient().marketing;
  const user = useAuthenticationStore((s) => s.user);
  const isSuperuser = user?.info?.activeContext?.contextType === "superuser";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [errors, setErrors] = useState([]);
  const [mode, setMode] = useState("visual");
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [showArchives, setShowArchives] = useState(false);
  const [workspaceView, setWorkspaceView] = useState("live");
  const [previewEmail, setPreviewEmail] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [publishedMetrics, setPublishedMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState("");
  const workflow =
    data?.marketingWorkflows?.find((item) => item.id === workflowId) || null;
  const publishedVersions = orderedWorkflowVersions(workflow).filter(
    (v) => v.status !== "DRAFT",
  );
  const selectedVersion =
    publishedVersions.find((v) => v.id === selectedVersionId) ||
    publishedVersions.find((v) => v.id === workflow?.defaultVersion?.id) ||
    publishedVersions.find((v) => v.status !== "ARCHIVED") ||
    null;
  const workflowTypeMetadata = data?.marketingWorkflowTypes?.find(
    (item) => item.code === workflow?.workflowType,
  );
  const criteria =
    workflowTypeMetadata?.supportedCriteria || data?.marketingCriteria || [];
  const personalisationFields = workflowTypeMetadata?.personalisationFields;
  const senders = data?.marketingSenders || [];
  const utmSuggestions = data?.marketingUtmSuggestions || {};
  const editor = useWorkflowEditor(workflow);
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("md"));
  const metricsRequest = useRef(0);
  const loadMetrics = useCallback(async () => {
    const token = ++metricsRequest.current;
    setPublishedMetrics(null);
    setMetricsLoading(true);
    setMetricsError("");
    try {
      const result = await api.getPublishedWorkflowMetrics(
        workflowId,
        selectedVersion?.id,
      );
      if (token === metricsRequest.current) setPublishedMetrics(result);
    } catch (error) {
      if (token === metricsRequest.current) setMetricsError(error.message);
    } finally {
      if (token === metricsRequest.current) setMetricsLoading(false);
    }
  }, [api, workflowId, selectedVersion?.id]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await api.getManagement();
      setData(next);
      editor.hydrate(
        next.marketingWorkflows?.find((item) => item.id === workflowId) || null,
      );
    } catch (e) {
      setErrors([{ message: e.message }]);
    } finally {
      setLoading(false);
    }
  }, [api, editor.hydrate, workflowId]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (narrow) setMode("list");
  }, [narrow]);
  useEffect(() => {
    if (workflow && !selectedVersion) setWorkspaceView("draft");
    if (workflow && !workflow.draftVersion) setWorkspaceView("live");
  }, [workflow, selectedVersion]);
  useEffect(() => {
    if (selectedVersion) loadMetrics();
    else setPublishedMetrics(null);
    return () => {
      metricsRequest.current += 1;
    };
  }, [selectedVersion?.id, loadMetrics, data]);
  const navigationGuard = useUnsavedChangesGuard(editor.state.dirty);
  const setResult = (result, successMessage) => {
    enqueueSnackbar(
      result?.success
        ? successMessage
        : result?.errors
            ?.map(
              (item) => `${item.path ? `${item.path}: ` : ""}${item.message}`,
            )
            .join(" ") ||
            result?.message ||
            "The request could not be completed.",
      { variant: result?.success ? "success" : "error" },
    );
    return Boolean(result?.success);
  };
  const reportRequestError = (error) => {
    enqueueSnackbar(error.message || "The request could not be completed.", {
      variant: "error",
    });
  };
  const save = useCallback(async () => {
    if (!workflow?.draftVersion) return null;
    setBusy("saving");
    setErrors([]);
    try {
      const result = await api.saveDraft({
        versionId: workflow.draftVersion.id,
        ...editor.input,
      });
      if (!setResult(result, "Draft saved")) return null;
      const refreshed = await api.getManagement();
      setData(refreshed);
      const current = refreshed.marketingWorkflows.find(
        (item) => item.id === workflowId,
      );
      if (!current) throw new Error("The workflow could not be reloaded.");
      editor.hydrate(current);
      return current;
    } catch (error) {
      reportRequestError(error);
      return null;
    } finally {
      setBusy("");
    }
  }, [api, workflow, workflowId, editor.input, editor.hydrate]);
  const createDraft = async () => {
    setBusy("creating");
    try {
      const result = await api.createDraft(workflow.id, selectedVersion?.id);
      setResult(result, "Draft created");
      await load();
      if (result.success) setWorkspaceView("draft");
    } catch (error) {
      reportRequestError(error);
    } finally {
      setBusy("");
    }
  };
  const validate = async () => {
    setBusy("validating");
    try {
      let current = workflow;
      if (editor.state.dirty) current = await save();
      if (!current) return;
      const result = await api.validateDraft(current.draftVersion.id);
      setResult(
        result,
        result.success ? "Draft is valid and ready to publish" : "",
      );
    } catch (error) {
      reportRequestError(error);
    } finally {
      setBusy("");
    }
  };
  const publish = async () => {
    try {
      let current = workflow;
      if (editor.state.dirty) current = await save();
      if (!current) return;
      const valid = await api.validateDraft(current.draftVersion.id);
      if (!setResult(valid, "Draft validated")) return;
      setConfirmation({
        title: "Publish workflow?",
        message:
          "Published definitions cannot be edited. Existing participants continue on their current version. Set this version as default separately to use it for automatic enrollment.",
        confirmLabel: "Publish",
        action: async () => {
          setBusy("publishing");
          const result = await api.publishDraft(current.draftVersion.id);
          setResult(result, "Workflow published");
          await load();
          if (result.success) {
            setSelectedVersionId(current.draftVersion.id);
            setWorkspaceView("live");
          }
        },
      });
    } catch (error) {
      reportRequestError(error);
    } finally {
      setBusy("");
    }
  };
  const stopVersion = async () => {
    const version = selectedVersion;
    setConfirmation({
      title: `Stop version ${version.versionNumber}?`,
      message: `Participants remain parked at their current stage. ${workflow.defaultVersion?.id === version.id ? "This is the default: stopping it leaves no default and disables automatic enrollment until you select another live version." : "Other versions continue unchanged."}`,
      confirmLabel: "Stop version",
      confirmColor: "error",
      action: async () => {
        setBusy("stopping-version");
        try {
          const result = await api.stopVersion(
            version.id,
            workflow.defaultVersion?.id === version.id,
          );
          if (!result.success) throw new Error(result.message);
          enqueueSnackbar(result.message, { variant: "success" });
          await load();
        } catch (error) {
          reportRequestError(error);
        } finally {
          setBusy("");
        }
      },
    });
  };
  const restartVersion = async (version) => {
    setBusy("restarting-version");
    try {
      const result = await api.restartVersion(version.id);
      if (!result.success) throw new Error(result.message);
      enqueueSnackbar(result.message, { variant: "success" });
      await load();
      setWorkspaceView("live");
    } catch (error) {
      reportRequestError(error);
    } finally {
      setBusy("");
    }
  };
  const versionAction = (action) =>
    setConfirmation({
      title:
        action === "default"
          ? `Set version ${selectedVersion.versionNumber} as default?`
          : `Archive version ${selectedVersion.versionNumber}?`,
      message:
        action === "default"
          ? "Future automatic enrollments use this version. Existing participants stay on their current version."
          : `Permanently stop ${publishedMetrics?.active ?? "all"} parked participants and release their workflow type slots. History remains available. This version cannot be resumed; you can create a new draft from it.`,
      confirmLabel: action === "default" ? "Set as default" : "Archive",
      action: async () => {
        setBusy(action);
        try {
          const result = await api.manageVersion(selectedVersion.id, action);
          if (
            setResult(
              result,
              action === "default" ? "Default updated" : "Version archived",
            )
          )
            await load();
        } catch (error) {
          reportRequestError(error);
        } finally {
          setBusy("");
        }
      },
    });
  const togglePause = async () => {
    setConfirmation({
      title: `${workflow.isEnabled ? "Pause" : "Resume"} workflow?`,
      message: `${workflow.isEnabled ? "Pause" : "Resume"} workflow email delivery?`,
      confirmLabel: workflow.isEnabled ? "Pause" : "Resume",
      action: togglePauseConfirmed,
    });
  };
  const togglePauseConfirmed = async () => {
    setBusy("pausing");
    try {
      const settings = {
        name: workflow.name,
        description: workflow.description,
        sendWindowStart: workflow.sendWindowStart,
        sendWindowEnd: workflow.sendWindowEnd,
        sendTimezone: workflow.sendTimezone,
        activeWeekdays: workflow.activeWeekdays,
        isEnabled: !workflow.isEnabled,
      };
      const result = await api.updateWorkflow(workflow.id, settings);
      setResult(
        result,
        workflow.isEnabled ? "Workflow paused" : "Workflow resumed",
      );
      await load();
    } catch (error) {
      reportRequestError(error);
    } finally {
      setBusy("");
    }
  };
  const deleteDraft = async () => {
    setConfirmation({
      title: "Delete draft?",
      message: "Delete this entire draft? This cannot be undone.",
      confirmLabel: "Delete",
      confirmColor: "error",
      action: deleteDraftConfirmed,
    });
  };
  const deleteDraftConfirmed = async () => {
    try {
      const result = await api.deleteDraft(workflow.draftVersion.id);
      setResult(result, "Draft deleted");
      await load();
    } catch (error) {
      reportRequestError(error);
    }
  };
  const ensureSaved = async (email) => {
    let current = workflow;
    if (editor.state.dirty) current = await save();
    if (!current) return null;
    const stage = current.draftVersion?.stages.find(
      (s) => s.key === editor.selectedStage?.key,
    );
    return stage?.templates.find((item) => item.order === email.order) || null;
  };
  const transition = useMemo(
    () =>
      editor.state.transitions.find(
        (e) => e.sourceKey === editor.selectedStage?.key,
      ),
    [editor.state.transitions, editor.selectedStage],
  );
  if (!isSuperuser) return <Navigate to="/" replace />;
  if (loading && !data)
    return (
      <FruitifyContainer>
        <CircularProgress />
      </FruitifyContainer>
    );
  if (!workflow)
    return (
      <FruitifyContainer>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h4">Workflow not found</Typography>
          <Typography color="text.secondary" my={2}>
            This workflow does not exist or is no longer available.
          </Typography>
          <Button
            component={RouterLink}
            to="/superuser/marketing/workflows"
            startIcon={<ArrowBack />}
          >
            Back to workflows
          </Button>
        </Paper>
      </FruitifyContainer>
    );
  return (
    <FruitifyContainer>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Button
              component={RouterLink}
              to="/superuser/marketing/workflows"
              startIcon={<ArrowBack />}
              sx={{ mb: 1 }}
            >
              Workflows
            </Button>
            <Typography variant="h4">Workflow</Typography>
            <Stack direction="row" spacing={1} mt={1}>
              <Chip
                color={workflow.isEnabled ? "success" : "default"}
                label={workflow.isEnabled ? "Active" : "Paused"}
              />
              <Chip
                variant="outlined"
                label={
                  workflow.defaultVersion
                    ? `Default v${workflow.defaultVersion.versionNumber}`
                    : "No default — automatic enrollment off"
                }
              />
              {workflow.draftVersion && (
                <Chip
                  color="warning"
                  variant="outlined"
                  label={`Draft v${workflow.draftVersion.versionNumber}`}
                />
              )}
              {editor.state.dirty && (
                <Chip color="warning" label="Unsaved changes" />
              )}
            </Stack>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            alignItems="center"
            alignSelf="flex-start"
          >
            {workflow.draftVersion ? (
              <>
                <Tooltip title="Save draft">
                  <span>
                    <IconButton
                      aria-label="Save draft"
                      onClick={save}
                      disabled={Boolean(busy) || !editor.state.dirty}
                    >
                      <Save />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Validate draft">
                  <span>
                    <IconButton
                      aria-label="Validate draft"
                      onClick={validate}
                      disabled={Boolean(busy)}
                    >
                      <Verified />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Publish draft">
                  <span>
                    <IconButton
                      aria-label="Publish draft"
                      onClick={publish}
                      disabled={Boolean(busy)}
                    >
                      <Publish />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete draft">
                  <span>
                    <IconButton
                      color="error"
                      aria-label="Delete draft"
                      onClick={deleteDraft}
                      disabled={Boolean(busy)}
                    >
                      <Delete />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Create editable draft">
                <span>
                  <IconButton
                    aria-label="Create editable draft"
                    onClick={createDraft}
                    disabled={Boolean(busy)}
                  >
                    <Add />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip
              title={workflow.isEnabled ? "Pause workflow" : "Resume workflow"}
            >
              <span>
                <IconButton
                  aria-label={
                    workflow.isEnabled ? "Pause workflow" : "Resume workflow"
                  }
                  onClick={togglePause}
                  disabled={Boolean(busy)}
                >
                  {workflow.isEnabled ? <Pause /> : <PlayArrow />}
                </IconButton>
              </span>
            </Tooltip>
            {selectedVersion?.status === "LIVE" && (
              <Tooltip title="Stop live version">
                <span>
                  <IconButton
                    color="error"
                    aria-label="Stop live version"
                    onClick={stopVersion}
                    disabled={Boolean(busy)}
                  >
                    <StopCircle />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        <MarketingAutomationControl
          settings={data.marketingAutomationSettings}
          api={api}
          onChanged={load}
        />
        {errors.length > 0 && (
          <Alert severity="error">
            <Typography fontWeight={700}>Please fix the following:</Typography>
            {errors.map((e, i) => (
              <Typography key={`${e.path}-${i}`} variant="body2">
                {e.path ? `${e.path}: ` : ""}
                {e.message}
              </Typography>
            ))}
          </Alert>
        )}
        {publishedVersions.length > 0 && workflow.draftVersion && (
          <Tabs
            value={workspaceView}
            onChange={(_, value) => setWorkspaceView(value)}
            aria-label="Published and draft workflow views"
          >
            <Tab value="live" label="Published versions" />
            <Tab value="draft" label="Draft workflow" />
          </Tabs>
        )}
        {publishedVersions.length > 0 && workspaceView === "live" && (
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <TextField
                select
                label="Workflow version"
                value={selectedVersion?.id || ""}
                onChange={(e) => {
                  setSelectedVersionId(e.target.value);
                  setWorkspaceView("live");
                }}
              >
                {publishedVersions
                  .filter(
                    (v) =>
                      showArchives ||
                      v.status !== "ARCHIVED" ||
                      v.id === selectedVersion?.id,
                  )
                  .map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      Version {v.versionNumber} · {v.status.toLowerCase()}
                      {v.id === workflow.defaultVersion?.id ? " · Default" : ""}
                    </MenuItem>
                  ))}
              </TextField>
              <FormControlLabel
                control={
                  <Switch
                    checked={showArchives}
                    onChange={(e) => setShowArchives(e.target.checked)}
                  />
                }
                label="Include archived versions"
              />
              {selectedVersion && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {selectedVersion.status === "LIVE" &&
                    selectedVersion.id !== workflow.defaultVersion?.id && (
                      <Button
                        disabled={Boolean(busy)}
                        onClick={() => versionAction("default")}
                      >
                        Set as default
                      </Button>
                    )}
                  {selectedVersion.status === "STOPPED" && (
                    <>
                      <Button
                        disabled={Boolean(busy)}
                        onClick={() => restartVersion(selectedVersion)}
                      >
                        Resume version
                      </Button>
                      <Button
                        color="error"
                        disabled={Boolean(busy)}
                        onClick={() => versionAction("archive")}
                      >
                        Archive version
                      </Button>
                    </>
                  )}
                  <Button
                    disabled={Boolean(busy) || Boolean(workflow.draftVersion)}
                    onClick={createDraft}
                  >
                    Create draft from version {selectedVersion.versionNumber}
                  </Button>
                </Stack>
              )}
            </Stack>
          </Paper>
        )}
        {selectedVersion && workspaceView === "live" && (
          <>
            <WorkflowSegments
              api={api}
              workflow={workflow}
              versionId={selectedVersion.id}
            />
            <ParticipantEnrollment
              api={api}
              workflow={workflow}
              versionId={selectedVersion.id}
              onEnrolled={load}
              disabled={
                selectedVersion.status !== "LIVE" ||
                !workflow.isEnabled ||
                data.marketingAutomationSettings?.isEnabled === false
              }
              disabledReason={
                selectedVersion.status !== "LIVE"
                  ? "Choose a live version to enroll participants."
                  : data.marketingAutomationSettings?.isEnabled === false
                    ? "Resume global marketing automation before enrolling participants."
                    : "Resume this workflow before enrolling participants. Publishing a version does not activate a paused workflow."
              }
            />
            <PublishedWorkflowOverview
              version={selectedVersion}
              workflow={workflow}
              criteria={criteria}
              metrics={publishedMetrics}
              loading={metricsLoading}
              error={metricsError}
              onRefresh={loadMetrics}
              narrow={narrow}
            />
            <WorkflowDeliverySchedule
              workflow={workflow}
              api={api}
              onChanged={load}
            />
            <WorkflowSenderPool
              workflow={workflow}
              senders={senders}
              api={api}
              onChanged={load}
            />
            <WorkflowOperations
              key={selectedVersion.id}
              version={selectedVersion}
              workflow={workflow}
              workflows={data.marketingWorkflows || []}
              api={api}
              onChanged={load}
            />
            <VersionContinuation
              key={selectedVersion.id}
              workflow={workflow}
              source={selectedVersion}
              api={api}
              onChanged={load}
            />
            <WorkflowMonitoring
              key={selectedVersion.id}
              versionId={selectedVersion.id}
              workflow={workflow}
              api={api}
            />
          </>
        )}
        {workflow.draftVersion && workspaceView === "draft" ? (
          <>
            <Typography variant="h5">Draft workflow</Typography>
            <WorkflowSenderPool
              workflow={workflow}
              senders={senders}
              api={api}
              onChanged={load}
            />
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Workflow name"
                    value={editor.state.workflow?.name || ""}
                    onChange={(e) =>
                      editor.dispatch({
                        type: "workflow",
                        value: { name: e.target.value },
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Timezone"
                    value={editor.state.workflow?.sendTimezone || ""}
                    onChange={(e) =>
                      editor.dispatch({
                        type: "workflow",
                        value: { sendTimezone: e.target.value },
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Send from"
                    InputLabelProps={{ shrink: true }}
                    value={(
                      editor.state.workflow?.sendWindowStart || "09:00"
                    ).slice(0, 5)}
                    onChange={(e) =>
                      editor.dispatch({
                        type: "workflow",
                        value: { sendWindowStart: e.target.value },
                      })
                    }
                  />
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Active delivery days
                  </Typography>
                  <ToggleButtonGroup
                    value={editor.state.workflow?.activeWeekdays || []}
                    onChange={(_, activeWeekdays) =>
                      editor.dispatch({
                        type: "workflow",
                        value: { activeWeekdays },
                      })
                    }
                    aria-label="Active workflow delivery days"
                    size="small"
                    sx={{ flexWrap: "wrap" }}
                  >
                    {WEEKDAYS.map((day, index) => (
                      <ToggleButton key={day} value={index} aria-label={day}>
                        {day}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {editor.state.workflow?.activeWeekdays?.length === 0 && (
                    <Typography color="error" variant="caption" display="block">
                      Select at least one active delivery day.
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Send until"
                    InputLabelProps={{ shrink: true }}
                    value={(
                      editor.state.workflow?.sendWindowEnd || "17:00"
                    ).slice(0, 5)}
                    onChange={(e) =>
                      editor.dispatch({
                        type: "workflow",
                        value: { sendWindowEnd: e.target.value },
                      })
                    }
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={editor.state.workflow?.description || ""}
                    onChange={(e) =>
                      editor.dispatch({
                        type: "workflow",
                        value: { description: e.target.value },
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Paper>
            <Tabs
              value={mode}
              onChange={(_, value) => setMode(value)}
              aria-label="Workflow editor mode"
            >
              <Tab value="visual" label="Visual flow" disabled={narrow} />
              <Tab value="list" label="Accessible list" />
            </Tabs>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 7 }}>
                {mode === "visual" ? (
                  <WorkflowCanvas
                    stages={editor.state.stages}
                    transitions={editor.state.transitions}
                    criteria={criteria}
                    selectedKey={editor.state.selectedStageKey}
                    dispatch={editor.dispatch}
                  />
                ) : (
                  <WorkflowStageList
                    stages={editor.state.stages}
                    selectedKey={editor.state.selectedStageKey}
                    dispatch={editor.dispatch}
                    criterion={criteria[0]}
                  />
                )}
                {mode === "visual" && (
                  <Button
                    sx={{ mt: 1 }}
                    startIcon={<Add />}
                    onClick={() =>
                      editor.dispatch({
                        type: "addStage",
                        criterion: criteria[0],
                      })
                    }
                  >
                    Add stage
                  </Button>
                )}
              </Grid>
              <Grid size={{ xs: 12, lg: 5 }}>
                <StageInspector
                  stage={editor.selectedStage}
                  stages={editor.state.stages}
                  criteria={criteria}
                  senders={senders}
                  transition={transition}
                  dispatch={editor.dispatch}
                  onPreview={setPreviewEmail}
                  utmSuggestions={utmSuggestions}
                  personalisationFields={personalisationFields}
                />
              </Grid>
            </Grid>
          </>
        ) : !workflow.draftVersion && workspaceView === "draft" ? (
          <Alert
            severity="info"
            action={<Button onClick={createDraft}>Create draft</Button>}
          >
            The published workflow is read-only. Create a draft to make changes.
          </Alert>
        ) : null}
      </Stack>
      <EmailPreviewDialog
        open={Boolean(previewEmail)}
        onClose={() => setPreviewEmail(null)}
        api={api}
        template={previewEmail}
        ensureSaved={ensureSaved}
        workflowId={workflow?.id}
        workflow={workflow}
        senders={workflow?.senders || []}
      />
      <ConfirmationDialog
        open={Boolean(confirmation)}
        title={confirmation?.title || "Confirm action"}
        message={confirmation?.message || "Continue?"}
        confirmLabel={confirmation?.confirmLabel}
        confirmColor={confirmation?.confirmColor}
        onCancel={() => setConfirmation(null)}
        onConfirm={async () => {
          const action = confirmation?.action;
          setConfirmation(null);
          await action?.();
        }}
      />
      <ConfirmationDialog
        open={Boolean(navigationGuard.pendingTransition)}
        title="Leave without saving?"
        message="You have unsaved workflow changes."
        confirmLabel="Leave"
        confirmColor="error"
        onCancel={navigationGuard.stay}
        onConfirm={navigationGuard.leave}
      />
    </FruitifyContainer>
  );
}
